/**
 * Helpers de DOM. Regla del proyecto: ningún dato del plano o de la API pasa por innerHTML/outerHTML/insertAdjacentHTML.
 * Los textos entran por createTextNode / textContent y los atributos por setAttribute.
 */
const SVG_NS = "http://www.w3.org/2000/svg";

export type Attr = string | number | boolean | null | undefined;
export type Attrs = Record<string, Attr>;
export type Kid = Node | string | null | false | undefined;

function apply(el: Element, attrs?: Attrs, kids?: Kid[]): void {
  if (attrs) {
    for (const k in attrs) {
      const v = attrs[k];
      if (v === null || v === undefined || v === false) continue;
      el.setAttribute(k, v === true ? "" : String(v));
    }
  }
  if (kids) {
    for (const kid of kids) {
      if (kid === null || kid === undefined || kid === false) continue;
      el.appendChild(typeof kid === "string" ? document.createTextNode(kid) : kid);
    }
  }
}

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Attrs, ...kids: Kid[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  apply(el, attrs, kids);
  return el;
}

export function s<K extends keyof SVGElementTagNameMap>(tag: K, attrs?: Attrs, ...kids: Kid[]): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  apply(el, attrs, kids);
  return el;
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export const round1 = (n: number): number => Math.round(n * 10) / 10;

/* ---------------------------- marcas dentro de la butaca -------------------------- */

export type MarkKind = "check" | "cross" | "dots" | "slash" | "access";

/** Trazo (sin relleno) de cada símbolo, centrado en (x, y) y dimensionado según el radio de la butaca. */
export function markPath(kind: MarkKind, x: number, y: number, r: number): string {
  const u = r * 0.62;
  const p = (dx: number, dy: number) => `${round1(x + dx * u)} ${round1(y + dy * u)}`;
  switch (kind) {
    case "check":
      return `M${p(-0.62, 0.05)}L${p(-0.18, 0.5)}L${p(0.66, -0.46)}`;
    case "cross":
      return `M${p(-0.5, -0.5)}L${p(0.5, 0.5)}M${p(0.5, -0.5)}L${p(-0.5, 0.5)}`;
    case "dots":
      return `M${p(-0.7, 0)}h.01M${p(0, 0)}h.01M${p(0.7, 0)}h.01`;
    case "slash":
      return `M${p(-0.6, 0.6)}L${p(0.6, -0.6)}`;
    case "access": {
      // cabeza, tronco con brazo y rueda: pictograma de silla de ruedas esquemático
      const hr = 0.2 * u;
      const hx = x + 0.05 * u;
      const hy = y - 0.88 * u;
      return (
        `M${round1(hx - hr)} ${round1(hy)}a${round1(hr)} ${round1(hr)} 0 1 0 ${round1(2 * hr)} 0a${round1(hr)} ${round1(hr)} 0 1 0 ${round1(-2 * hr)} 0` +
        `M${p(0.05, -0.55)}V${round1(y + 0.05 * u)}H${round1(x + 0.75 * u)}L${p(1, 0.85)}` +
        `M${p(0.05, -0.25)}h${round1(0.5 * u)}` +
        `M${p(-0.2, -0.1)}A${round1(0.62 * u)} ${round1(0.62 * u)} 0 1 0 ${p(0.5, 0.62)}`
      );
    }
  }
}

/** Iconos de trazo de 24x24 (se pintan con currentColor). */
export const icon = (name: "plus" | "minus" | "reset" | "clock" | "close" | "info" | "warn" | "alert"): SVGSVGElement => {
  const svg = s("svg", { viewBox: "0 0 24 24", width: 18, height: 18, "aria-hidden": "true", focusable: "false", class: "ic" });
  const d: Record<string, string> = {
    plus: "M12 5v14M5 12h14",
    minus: "M5 12h14",
    reset: "M4 9V4h5M20 15v5h-5M4 4l6 6M20 20l-6-6",
    clock: "M12 7v5l3 2M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18z",
    close: "M6 6l12 12M18 6L6 18",
    info: "M12 11v5M12 8h.01M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18z",
    warn: "M12 9v4M12 17h.01M10.3 4.2L2.8 17.5A2 2 0 0 0 4.5 20.5h15a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0z",
    alert: "M12 8v5M12 16.5h.01M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18z",
  };
  svg.appendChild(s("path", { d: d[name], fill: "none", stroke: "currentColor", "stroke-width": 2, "stroke-linecap": "round", "stroke-linejoin": "round" }));
  return svg;
};
